import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { AccountUsersModule } from '@capital-access/account/users';
import { CommonFormattingModule } from '@capital-access/common/formatting';
import { CommonLocalizationModule, LocalizationService } from '@capital-access/common/localization';
import {
  ChipSize,
  ChipType,
  FireflyChipModule,
  FireflyDrawerContent,
  FireflyDrawerModule
} from '@capital-access/firefly/components';
import { PriorityRating, PriorityRatingLocalized, TargetCompany } from '@capital-access/targeting/common';
import { confirmEditTarget, confirmResetRating } from '../../+store/data/actions';
import { CUSTOM_TARGET_LOCALIZATION_SCOPE } from '../../constants';

@Component({
  selector: 'ca-customize-target',
  standalone: true,
  imports: [
    CommonModule,
    FireflyDrawerModule,
    FireflyChipModule,
    CommonLocalizationModule,
    AccountUsersModule,
    CommonFormattingModule
  ],
  templateUrl: './customize-target.component.html',
  styleUrls: ['./customize-target.component.scss']
})
export class CustomizeTargetComponent extends FireflyDrawerContent<{ targetCompany: TargetCompany }> {
  targetCompanyData!: TargetCompany;
  selectedInstitutionRating?: PriorityRating;
  initialInstitutionRating?: PriorityRating;

  institutionRatingItems = Object.values(PriorityRating)
    .filter(v => v !== PriorityRating.NR)
    .map(id => ({ id, value: PriorityRatingLocalized[id], disabled: false, iconPopoverContent: '' }));

  localizationScope = CUSTOM_TARGET_LOCALIZATION_SCOPE;
  chipSize = ChipSize.Sm;
  chipType = ChipType.SingleChoice;

  constructor(private readonly localizationService: LocalizationService, private store: Store) {
    super();
  }

  override onInjectInputs(input: { targetCompany: TargetCompany }) {
    this.targetCompanyData = input.targetCompany;
    if (this.targetCompanyData.customTargetRating) {
      this.initialInstitutionRating = this.targetCompanyData.customTargetRating.customRating;
    } else {
      this.initialInstitutionRating = this.targetCompanyData.priorityRating || PriorityRating.NR;
    }

    this.localizationService
      .localize('cannotUpdateToSystemRating', {}, { scope: this.localizationScope })
      .pipe(take(1))
      .subscribe(msg => {
        this.institutionRatingItems = Object.values(PriorityRating)
          .filter(v => v !== PriorityRating.NR)
          .map(id => ({
            id,
            value: PriorityRatingLocalized[id],
            disabled: id === this.initialInstitutionRating,
            iconPopoverContent: id === this.initialInstitutionRating ? msg : ''
          }));
      });
  }

  onSelect(selected: unknown) {
    const item = selected as {
      id: PriorityRating;
      value: PriorityRatingLocalized;
      disabled?: boolean;
      iconPopoverContent?: string;
    };
    // prevent selecting the chip that was active when drawer opened
    if (item.disabled) {
      return;
    }
    this.selectedInstitutionRating = item.id;
  }

  getChipType(item: Record<string, unknown>) {
    return item['id'] === this.selectedInstitutionRating ? ChipType.SingleChoice : ChipType.Enabled;
  }

  get ratingValue() {
    return this.targetCompanyData.priorityRating === PriorityRating.NR ? null : this.targetCompanyData.priorityRating;
  }

  get localizedPriorityRatingValue() {
    const priorityRating = this.targetCompanyData.priorityRating;
    switch (priorityRating) {
      case PriorityRating.HM:
        return 'OV';
      case PriorityRating.HT:
        return 'UN';
      default:
        return priorityRating;
    }
  }

  onSave() {
    this.store.dispatch(
      confirmEditTarget({
        targetCompany: this.targetCompanyData,
        priorityRating: this.selectedInstitutionRating!
      })
    );
  }

  onCancel() {
    this.close();
  }

  onReset() {
    this.store.dispatch(confirmResetRating({ targetCompany: this.targetCompanyData }));
  }
}
